import { useState, type ReactNode } from 'react';
import { useCan } from '../../auth';
import { PageHead, Q, Section } from '../../ui/kit';
import { NOT_APPLIED, SaveBar, ThreatOffBanner, lines, threatOff, useProtectionSettings, useSaveProtection, type ProtectionSettingsResponse, type SaveResult } from './common';

/** Protect → Threat protection → Downloads (P5): /protect/threats/downloads. */

interface DownloadSettings { enabled: boolean; blockKnownMalicious: boolean; warnUnknownExecutables: boolean; serverLookup: boolean; executableExtensions: string[] }

export function DownloadProtection() {
  const q = useProtectionSettings();
  return (
    <>
      <PageHead
        title="Download protection"
        sub="Stops files from known malicious sources, and can warn before people run installers from sites nobody knows. Decisions are made in the browser where possible; Ward’s server is asked only about downloads the browser cannot decide."
      />
      <Q q={q}>{(d) => <DownloadForm d={d} />}</Q>
    </>
  );
}

function Option({ id, label, checked, disabled, onChange, children }: { id: string; label: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <div>
      <label className="check">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-describedby={`${id}-explain`} data-testid={id} />
        <b>{label}</b>
      </label>
      <p className="explain m-0 mt-2 ml-24" id={`${id}-explain`}>{children}</p>
    </div>
  );
}

function DownloadForm({ d }: { d: ProtectionSettingsResponse }) {
  const can = useCan();
  const writable = can('settings:write');
  const [s, setS] = useState(d.settings.downloads as DownloadSettings);
  const [exts, setExts] = useState((d.settings.downloads.executableExtensions as string[]).join(', '));
  const [saved, setSaved] = useState<SaveResult | null>(null);
  const save = useSaveProtection(setSaved);
  const upd = (p: Partial<DownloadSettings>) => { setS({ ...s, ...p }); setSaved(null); };
  const off = !s.enabled;
  const serverOff = threatOff(d);
  return (
    <>
      {serverOff && <ThreatOffBanner />}
      <fieldset className="m-0 p-0 border-0" disabled={!writable}>
        <Section title="Protection">
          <div className="stack-12 mt-12">
            <Option id="downloads-enabled" label="Protect downloads" checked={s.enabled} onChange={(v) => upd({ enabled: v })}>
              Applies to every file downloaded in an enrolled browser.{serverOff && NOT_APPLIED}
            </Option>
            <Option id="downloads-block-malicious" label="Block downloads from known malicious sources" checked={s.blockKnownMalicious} disabled={off} onChange={(v) => upd({ blockKnownMalicious: v })}>
              The download is stopped when its site, address or file is on a threat intelligence feed or one of your deny lists (Threat protection → Threat intel). People see why and who to contact.
            </Option>
            <Option id="downloads-warn-unknown" label="Warn before executable files from unfamiliar sites" checked={s.warnUnknownExecutables} disabled={off} onChange={(v) => upd({ warnUnknownExecutables: v })}>
              Installers and scripts from sites outside a list of popular websites show a warning people can continue past. Off by default: useful where software comes from a few well-known places, noisy where people download from many small vendors.
            </Option>
            <Option id="downloads-server-lookup" label="Check undecided downloads with Ward’s server" checked={s.serverLookup} disabled={off} onChange={(v) => upd({ serverLookup: v })}>
              Sends the download’s address (and its file hash when the browser knows it) to your Ward server, which compares it with the full threat intelligence — never the file itself, and nothing is stored. If the server cannot be reached, the download continues and the event is recorded; it is never blocked silently.
            </Option>
          </div>
        </Section>
        <details className="mt-16" data-testid="downloads-extensions-details">
          <summary><b>Executable file types</b> <span className="sub">{lines(exts).length} types · used by the unfamiliar-sites warning</span></summary>
          <label className="field mt-12">File extensions <span className="hint">comma-separated, without the dot</span>
            <textarea rows={3} value={exts} disabled={off} onChange={(e) => { setExts(e.target.value); setSaved(null); }} data-testid="downloads-extensions" />
          </label>
        </details>
        <details className="mt-12">
          <summary><b>Expected impact</b></summary>
          <p className="explain">Blocking known malicious downloads rarely affects legitimate work: it acts only on evidence from feeds and your lists. Sites on your allow lists (Threat protection → Web and Threat intel) are not treated as malicious. Every block and warning is reported as an event with the category, the site’s host name and the file type — not the full address.</p>
        </details>
      </fieldset>
      <SaveBar writable={writable} busy={save.isPending} error={save.error} saved={saved}
        onSave={() => save.mutate({ downloads: { ...s, executableExtensions: lines(exts).map((x) => x.replace(/^\./, '')) } })} />
    </>
  );
}
